import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  activateLicenses,
  emptyLicenseSummary,
  fetchLicenseProviders,
  fetchLicenseSummaryId_company,
  fetchLicenseUuid,
  LICENSE_OPTIONS,
  LicenseOption,
  LicenseSummary,
  removeLicense,
} from "../../../api/license";
import {
  DeviceWithoutLegacyPassword,
  LicenseProvider,
} from "../../../generated/types";
import { isAdminAccount } from "../../../utils/roles";
import { Button } from "../../common/Button";
import { Modal, ModalActions } from "../../common/Modal";
// Tables deplacees dans `LicenseBadge` : l'historique des licences en a besoin aussi.
import { licenseToI18nKey, licenseToIcon } from "./LicenseBadge";

/** Codes actifs remis dans l'ordre d'affichage de référence (l'API ne garantit pas l'ordre). */
const sortLicenses = (codes: LicenseOption[]): LicenseOption[] =>
  LICENSE_OPTIONS.filter((code) => codes.includes(code));

/**
 * Licences actives d'un produit.
 *
 * Chargées une seule fois par ticket et partagées entre le résumé (`LicenseSection`) et le
 * modal d'activation : `reload` resynchronise les deux après une sauvegarde. `enabled` évite
 * l'appel sur les produits qui n'ont pas de licences (tout sauf `serenity`).
 */
export const useDeviceLicenses = (uuid: string, enabled = true) => {
  const [activeCodes, setActiveCodes] = useState<LicenseOption[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    const { license } = await fetchLicenseUuid(uuid);
    setActiveCodes(sortLicenses(license));
    setIsLoading(false);
  }, [uuid, enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { activeCodes, isLoading, reload };
};

export const LicenseSection = ({
  activeCodes,
}: {
  activeCodes: LicenseOption[];
}) => {
  const { t } = useTranslation();

  if (activeCodes.length === 0) {
    return <div className="text-sm">-</div>;
  }

  return (
    <div className="flex flex-1 flex-wrap gap-2">
      {activeCodes.map((code) => (
        <span
          key={code}
          className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full text-xs font-oxaniumMedium bg-grey-light text-grey-dark"
        >
          <FontAwesomeIcon icon={licenseToIcon[code]} />
          <span>{t(licenseToI18nKey[code])}</span>
        </span>
      ))}
    </div>
  );
};

export const LicenseModal = ({
  device,
  companyId,
  activeCodes,
  isOpen,
  onClose,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  device: DeviceWithoutLegacyPassword;
  /**
   * Entreprise du CONTEXTE d'ouverture (page Comptes, Bâtiments, Produits…).
   *
   * ⚠️ Ce n'est PAS forcément l'entreprise du produit : `DeviceTicket` est rendu depuis
   * trois écrans, qui passent chacun le `company` de leur propre contexte. Ouvrir un
   * produit depuis la fiche d'un compte transmettait ainsi l'entreprise DU COMPTE.
   * Conservée pour compatibilité, mais `device.id_company` fait foi (voir
   * `stockOwnerCompanyId`).
   */
  companyId: number;
  /** Licences déjà activées côté serveur, telles que chargées par `useDeviceLicenses`. */
  activeCodes: LicenseOption[];
  /** Rechargement des licences du produit, à appeler après un enregistrement. */
  onSaved: () => Promise<void> | void;
}) => {
  const { t } = useTranslation();

  // Stock de l'entreprise (détenu / déjà consommé), tous produits confondus.
  const [summary, setSummary] = useState<LicenseSummary>(emptyLicenseSummary());
  // Sélection en cours : elle ne devient effective qu'à la sauvegarde.
  const [selected, setSelected] = useState<LicenseOption[]>(activeCodes);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Message de confirmation affiché SANS fermer la modale : l'utilisateur doit pouvoir
  // constater le résultat (compteurs à jour, cases cochées) avant de partir.
  const [success, setSuccess] = useState<string | null>(null);

  // Entreprise PROPRIÉTAIRE du produit : c'est elle qui détermine à la fois le stock à
  // consulter et les installateurs partenaires. On la lit sur le produit lui-même —
  // `companyId` dépend de l'écran d'où la modale a été ouverte, et se trompait donc dès
  // qu'on passait par une fiche compte ou bâtiment (chargement des compteurs de la
  // mauvaise entreprise, et aucun partenaire trouvé).
  const stockOwnerCompanyId = device.id_company || companyId;

  // Retirer une licence est réservé aux comptes admin / psc. Décocher une licence que l'on
  // vient de cocher reste permis à tout le monde : rien n'a encore été envoyé au serveur.
  const canDeactivate = isAdminAccount();

  // Un admin/PSC peut puiser dans le stock d'un INSTALLATEUR PARTENAIRE de l'entreprise
  // du produit, au lieu de celui de cette entreprise. Un installateur, lui, ne peut
  // puiser que chez lui : le sélecteur ne lui est pas proposé (et le back ignorerait le
  // champ de toute façon).
  const canChooseProvider = isAdminAccount();
  const [providers, setProviders] = useState<LicenseProvider[]>([]);
  const [providerId, setProviderId] = useState<number | null>(null);
  // Incrémenté après chaque sauvegarde pour forcer le rechargement du stock : le summary
  // ne dépend sinon que de l'entreprise et de l'installateur, et restait donc figé après
  // un retrait — le disponible affiché ne remontait pas de 1.
  const [stockRefresh, setStockRefresh] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSuccess(null);
    // `LicenseModal` reste MONTÉ quand la modale est fermée (c'est `Modal` qui rend
    // `null`) : sans cette remise à zéro, l'installateur choisi sur un produit
    // réapparaîtrait sélectionné sur le produit suivant, avec le risque de débiter
    // quelqu'un sans l'avoir voulu. La sélection ne survit qu'aux rechargements de
    // stock d'une MÊME session d'édition (voir l'effet des installateurs).
    setProviderId(null);
  }, [isOpen]);

  // Installateurs partenaires de l'entreprise du produit. Chargés seulement pour un
  // admin : la route est sous AdminMiddleware, un installateur récolterait un 403.
  useEffect(() => {
    if (!isOpen || !canChooseProvider || !stockOwnerCompanyId) return;

    let cancelled = false;
    fetchLicenseProviders(stockOwnerCompanyId).then(({ providers: list }) => {
      if (cancelled) return;
      setProviders(list);

      setProviderId((current) => {
        // Cet effet se relance aussi APRÈS une sauvegarde (le stock a changé, il faut
        // le recharger). Le choix de l'utilisateur doit alors SURVIVRE : le réinitialiser
        // l'obligerait à re-sélectionner l'installateur entre deux enregistrements, et
        // ferait disparaître le récapitulatif « X licences seront débitées chez… » qu'il
        // vient de valider.
        if (current !== null && list.some((p) => p.id_company === current)) {
          return current;
        }

        // Présélection UNIQUEMENT s'il n'existe qu'un seul installateur utilisable : il
        // n'y a alors aucun choix possible, donc aucun risque de débiter le mauvais.
        // Dès qu'il y en a deux, rien n'est présélectionné — un défaut invisible ferait
        // consommer le stock de quelqu'un sans que l'admin l'ait décidé, et une licence
        // attribuée doit être retirée pour être corrigée.
        const usable = list.filter((p) =>
          p.stock.some((item) => item.available > 0)
        );
        return usable.length === 1 ? usable[0].id_company : null;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, canChooseProvider, stockOwnerCompanyId, stockRefresh]);

  // La sélection repart toujours de l'état serveur : à l'ouverture, et après une sauvegarde
  // (y compris partielle, si un appel a échoué en cours de route).
  useEffect(() => {
    setSelected(activeCodes);
  }, [activeCodes, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    // Sans id d'entreprise, la route répondrait sur `/license/summary/undefined` et TOUS les
    // compteurs resteraient à zéro sans rien dire : on le signale au lieu de laisser croire
    // que l'entreprise ne possède aucune licence.
    //
    // Ne concerne que l'admin : un non-admin envoie `me`, que le serveur résout depuis la
    // session — il n'a donc pas besoin de connaître l'entreprise du produit pour afficher
    // SON stock, et bloquer ici le priverait de l'écran pour rien.
    if (canChooseProvider && !stockOwnerCompanyId) {
      setSummary(emptyLicenseSummary());
      setError(String(t("license-load-error")));
      return;
    }

    // Le stock affiché doit être celui qui sera RÉELLEMENT débité : celui de
    // l'installateur choisi par l'admin, ou à défaut celui de l'entreprise du produit.
    // Afficher l'un et débiter l'autre mènerait à un 409 incompréhensible.
    //
    // ⚠️ Un NON-admin puise dans SON propre stock, pas dans celui du produit : demander
    // l'entreprise du produit (l'éleveur) valait un 403 à tout installateur ouvrant les
    // licences d'un produit de son client. `me` fait résoudre l'entreprise par le serveur,
    // depuis la session — le front n'a pas l'id_company du compte connecté sous la main.
    const stockCompanyId = canChooseProvider
      ? (providerId ?? stockOwnerCompanyId)
      : "me";

    let cancelled = false;

    fetchLicenseSummaryId_company(String(stockCompanyId)).then(
      ({ license, error: summaryError }) => {
        if (cancelled) return;
        setSummary(license ?? emptyLicenseSummary());
        if (summaryError) setError(summaryError);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    canChooseProvider,
    stockOwnerCompanyId,
    providerId,
    stockRefresh,
    t,
  ]);

  /** Une licence déjà active reste réactivable : son jeton n'a pas été rendu côté serveur. */
  const canActivate = (code: LicenseOption) => {
    const { total, active } = summary[code];
    return activeCodes.includes(code) || total - active > 0;
  };

  const isLocked = (code: LicenseOption) => {
    const wasActive = activeCodes.includes(code);
    const isSelected = selected.includes(code);
    return isSelected ? wasActive && !canDeactivate : !canActivate(code);
  };

  const toggleLicense = (code: LicenseOption) => {
    if (isLocked(code) || isSaving) return;
    // Toute nouvelle modification périme le message précédent : le laisser affiché
    // ferait croire que le changement en cours est déjà enregistré.
    setSuccess(null);
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  // Licences qui seront réellement activées : la sélection inclut celles déjà actives,
  // qui ne consomment pas de stock supplémentaire.
  const pendingActivations = selected.filter(
    (code) => !activeCodes.includes(code)
  ).length;

  // Y a-t-il quelque chose à enregistrer ? On compare des ENSEMBLES et non des tableaux :
  // l'ordre de `selected` suit les clics de l'utilisateur (cocher puis décocher puis
  // recocher), il ne dit rien de l'état final. Sans cette comparaison, le bouton resterait
  // actif après un aller-retour qui ne change rien, et déclencherait des appels inutiles.
  const hasChanges =
    pendingActivations > 0 ||
    activeCodes.some((code) => !selected.includes(code));

  const handleSave = async () => {
    const toActivate = selected.filter((code) => !activeCodes.includes(code));
    const toRemove = activeCodes.filter((code) => !selected.includes(code));

    // Un admin doit avoir choisi l'installateur AVANT d'activer : sans `providerId`, la
    // requête partirait sans `id_company_stock` et le back débiterait l'entreprise du
    // produit — l'éleveur, qui n'a pas de stock. Le retrait, lui, n'a pas besoin de
    // source : il rend les licences à leur détenteur réel (retrouvé côté serveur).
    if (canChooseProvider && toActivate.length > 0 && !providerId) {
      setError(String(t("license-choose-provider-first")));
      return;
    }

    if (toActivate.length === 0 && toRemove.length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    setError(null);

    // Les retraits d'abord : ils libèrent du stock pour les activations du même enregistrement
    // (échanger une licence entre deux produits quand il n'en reste aucune de disponible).
    let failure: string | null = null;
    for (const code of toRemove) {
      const { error: removeError } = await removeLicense(device.uuid, code);
      if (removeError) {
        failure = removeError;
        break;
      }
    }

    if (!failure && toActivate.length > 0) {
      const { error: activateError } = await activateLicenses(
        device.uuid,
        toActivate,
        // `undefined` si aucun installateur n'est désigné : le back retombe alors sur
        // le stock de l'appelant, comportement historique.
        providerId ?? undefined
      );
      if (activateError) failure = activateError;
    }

    // Même en cas d'échec : les appels précédents ont pu passer, on resynchronise l'affichage
    // sur l'état réel du produit plutôt que de laisser la sélection mentir.
    await onSaved();
    // Le stock a changé (licences rendues ou consommées) : forcer son rechargement,
    // sinon les compteurs affichés restent ceux d'avant l'enregistrement.
    setStockRefresh((n) => n + 1);
    setIsSaving(false);

    if (failure) {
      setError(failure);
      setSuccess(null);
      return;
    }

    // La modale reste OUVERTE après un enregistrement réussi : la fermer masquait le
    // résultat de l'action (compteurs mis à jour, licences cochées) et obligeait à la
    // rouvrir pour vérifier ce qui s'était passé. L'utilisateur ferme quand il a vu.
    setSuccess(
      String(
        t("license-saved", {
          activated: toActivate.length,
          removed: toRemove.length,
        })
      )
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("device-licenses")}
      maxWidth="2xl"
    >
      {/* Pas de `max-h-screen` ici : la modale (Modal.tsx) gère déjà sa hauteur et son
          défilement. L'imposer empêchait la liste défilante ci-dessous de calculer la
          sienne sur les petits écrans. */}
      <div>
        {/* Choix de l'installateur dont le stock sera débité. Réservé aux admin/PSC :
            un installateur puise forcément chez lui.

            Section volontairement MISE EN AVANT (encadré, fond, icône) : débiter le
            stock d'un installateur est un mouvement de valeur, pas un réglage. Fondue
            dans la liste des licences, elle serait survolée. */}
        {canChooseProvider && (
          <div className="mb-3 rounded-lg border-2 border-tertiary bg-grey-light p-2">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon="briefcase" className="text-tertiary" />
              <p className="font-oxaniumMedium text-default-text">
                {t("license-stock-source")}
              </p>
            </div>

            {providers.length === 0 ? (
              // 204 côté back : aucun partenariat installateur accepté. Message
              // explicite — une liste vide passerait pour un bug.
              <p className="mt-2 text-sm text-grey-readable">
                {t("license-no-partner")}
              </p>
            ) : (
              <>
                {/* Borné aussi, et volontairement SERRÉ : ce bloc n'est qu'un choix
                    préalable, la liste des licences est le contenu principal. Au-delà de
                    3 partenaires il défile plutôt que de repousser les licences. */}
                <div className="mt-2 flex max-h-[168px] flex-col gap-1 overflow-y-auto pr-1">
                  {/* Un admin pioche FORCÉMENT chez un installateur partenaire :
                      l'éleveur n'a pas de stock propre. */}
                  {providers.map((provider) => {
                    const available = provider.stock.reduce(
                      (sum, item) => sum + item.available,
                      0
                    );
                    const isEmpty = available === 0;
                    const isSelected = providerId === provider.id_company;
                    return (
                      <button
                        key={provider.id_company}
                        type="button"
                        // Grisé ET non cliquable : proposer un installateur sans stock
                        // ferait échouer l'attribution en 409 après coup.
                        disabled={isEmpty}
                        aria-pressed={isSelected}
                        onClick={() => setProviderId(provider.id_company)}
                        // `flex-col` sous `sm` : le nom d'entreprise et le compteur de
                        // stock ne tiennent pas côte à côte sur un téléphone étroit — le
                        // compteur passait à la ligne en cassant l'alignement.
                        className={`flex w-full flex-col items-start gap-0.5 rounded-lg px-3 py-1 text-left transition-all sm:flex-row sm:items-center sm:justify-between sm:gap-3 ${
                          isEmpty
                            ? "bg-white text-grey-medium cursor-not-allowed opacity-60"
                            : isSelected
                              ? "bg-secondary text-grey-dark"
                              : "bg-white text-grey-dark hover:opacity-80 cursor-pointer"
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          {/* Pastille de sélection : l'état ne repose pas sur la seule
                              couleur de fond (daltonisme, impression). */}
                          <FontAwesomeIcon
                            icon={isSelected ? "check-circle" : "circle"}
                          />
                          <span className="font-oxaniumMedium">
                            {provider.company_name}
                          </span>
                        </span>
                        <span className="text-xs">
                          {isEmpty
                            ? t("license-no-stock")
                            : `${available} ${t("license-available-count")}`}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Rappel de la conséquence, là où l'admin regarde avant de valider.
                    Sans installateur choisi, l'avertissement remplace le récapitulatif. */}
                {!providerId ? (
                  <p className="mt-2 flex items-center gap-2 text-sm text-warning-readable">
                    <FontAwesomeIcon icon="triangle-exclamation" />
                    {t("license-choose-provider-first")}
                  </p>
                ) : (
                  pendingActivations > 0 && (
                    <p className="mt-2 text-sm text-default-text">
                      {t("license-will-debit", {
                        count: pendingActivations,
                        company:
                          providers.find((p) => p.id_company === providerId)
                            ?.company_name ?? "",
                      })}
                    </p>
                  )
                )}
              </>
            )}
          </div>
        )}

        {/* Liste défilante : sans hauteur bornée, les 10 licences repoussent le choix de
            l'installateur hors de vue en haut et les boutons en bas.
            La borne cible la HAUTEUR de la fenêtre, pas son orientation : `landscape:`
            désigne tout écran plus large que haut — un 1440x900 inclus — et appliquait
            donc la petite borne prévue pour le paysage mobile à tous les ordinateurs
            (4 licences visibles au lieu de 8).
            Une ligne fait ~62px : 220px par défaut (paysage mobile, ~375px de haut),
            relevé dès que la fenêtre dépasse 700px de haut. */}
        <div className="flex w-full flex-col space-y-1.5 overflow-y-auto pr-1 max-h-[220px] [@media(min-height:700px)]:max-h-[52vh] [@media(min-height:900px)]:max-h-[58vh]">
          {LICENSE_OPTIONS.map((code) => {
            const isSelected = selected.includes(code);
            const locked = isLocked(code);
            const { total, active } = summary[code];

            return (
              <button
                key={code}
                type="button"
                onClick={() => toggleLicense(code)}
                disabled={locked || isSaving}
                title={
                  locked && isSelected
                    ? String(t("license-remove-admin-only"))
                    : undefined
                }
                className={`flex w-full items-center px-3 py-1.5 rounded-lg transition-all ${
                  isSelected
                    ? "bg-secondary text-grey-dark"
                    : "bg-grey-light text-grey-dark"
                } ${
                  locked || isSaving
                    ? "cursor-not-allowed opacity-60"
                    : "hover:opacity-80 cursor-pointer"
                }`}
              >
                {/* `min-w-0` : sans lui, le texte impose sa largeur au conteneur flex et
                    la coche de droite sort de la carte sur un écran étroit. */}
                <div className="flex min-w-0 flex-row w-full items-center gap-3">
                  <FontAwesomeIcon icon={licenseToIcon[code]} />
                  <div className="min-w-0 justify-items-start">
                    <div className="text-base font-medium font-oxaniumMedium">
                      {t(licenseToI18nKey[code])}
                    </div>
                    {/* Compteurs de l'entreprise, toujours affichés — y compris à 0, pour
                        que la licence apparaisse quand même dans la liste. */}
                    <div className="text-sm text-grey-dark break-words">
                      {t("license-bought")} : {total} — {t("license-active")} :{" "}
                      {active}
                    </div>
                  </div>
                </div>
                {/* `shrink-0` : sans lui, un libellé long compresse l'icône jusqu'à la
                    rendre illisible sur écran étroit. */}
                <div className="shrink-0">
                  <FontAwesomeIcon icon={isSelected ? "check" : "times"} />
                </div>
              </button>
            );
          })}
        </div>

        {error && (
          <p className="mt-3 text-sm text-red-600 text-center">{error}</p>
        )}
        {success && !error && (
          <p className="mt-3 text-sm text-secondary text-center font-oxaniumMedium">
            {success}
          </p>
        )}
      </div>

      <ModalActions>
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
          disabled={isSaving}
        >
          {t("button-cancel")}
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={
            isSaving ||
            // Rien à enregistrer : évite un appel inutile et dit à l'utilisateur que sa
            // sélection est déjà celle du serveur. Vaut pour TOUS les rôles.
            !hasChanges ||
            // Aucun installateur partenaire : rien ne peut être activé. Le retrait reste
            // possible, d'où la condition sur les activations seulement.
            (canChooseProvider &&
              providers.length === 0 &&
              pendingActivations > 0)
          }
        >
          {t("button-save")}
        </Button>
      </ModalActions>
    </Modal>
  );
};
